import React, { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

import { ContactFormData, validateForm } from "@/utils/formValidation";
import { supabase } from "@/integrations/supabase/client";
import ContactFormFields from "@/components/contact/ContactFormFields";

interface ContactFormProps {
  onSuccess?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
    consent_given: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formLoadedAt = useRef(Date.now());
  const honeypotRef = useRef("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value, type } = e.target;
    
    if (id === "website_url") {
      honeypotRef.current = value;
      return;
    }
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [id]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check (bots fill hidden fields)
    if (honeypotRef.current) {
      // Silently pretend success to not alert the bot
      navigate('/danke');
      return;
    }

    // Time check: form filled too fast = bot
    const timeSpent = Date.now() - formLoadedAt.current;
    if (timeSpent < 3000) {
      toast({
        title: "Bitte nehmen Sie sich einen Moment Zeit",
        description: "Das Formular wurde zu schnell ausgefüllt. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm(formData, { toast })) return;
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: formData.subject || null,
          message: formData.message,
          consent_given: formData.consent_given,
          _t: formLoadedAt.current,
        },
      });

      if (error || (data && (data as any).error)) {
        const serverMsg = (data as any)?.error || (error as any)?.message || "Fehler beim Senden der Nachricht";
        handleError(new Error(serverMsg));
        return;
      }

      handleSuccess();
      
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccess = () => {
    if (onSuccess) onSuccess();
    navigate('/danke');
  };

  const handleError = (error: unknown) => {
    console.error("Contact form error:", error);
    const description =
      error instanceof Error && error.message
        ? error.message
        : "Es gab ein Problem beim Senden Ihrer Nachricht. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per E-Mail.";
    toast({
      title: "Fehler beim Senden",
      description,
      variant: "destructive",
    });
  };

  return (
    <ContactFormFields
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  );
};

export default ContactForm;
